import { useEffect, useState } from 'react';
import { getGTMConfig, initializeDataLayer, initializeConsentMode, updateConsent } from '@/lib/gtm';

const parseConsentCookie = (): Record<string, string> => {
  if (typeof document === 'undefined') return {};
  const raw = document.cookie
    .split('; ')
    .find((c) => c.startsWith('cookieyes-consent='))
    ?.split('=')[1];
  if (!raw) return {};
  const decoded = decodeURIComponent(raw);
  return Object.fromEntries(
    decoded
      .split('&')
      .map((entry) => entry.split('='))
      .filter(([key, value]) => key && value != null)
  );
};

export const GTMProvider = ({ children }: { children: React.ReactNode }) => {
  const [analyticsAllowed, setAnalyticsAllowed] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    window.dataLayer = window.dataLayer || [];
    window.gtag = window.gtag || function (...args: unknown[]) {
      window.dataLayer.push(args);
    };

    initializeConsentMode();

    const applyConsentFromCookie = () => {
      const consent = parseConsentCookie();
      const allowAnalytics = consent.analytics === 'yes' || consent.consent === 'yes';
      setAnalyticsAllowed(allowAnalytics);
      updateConsent({
        analytics_storage: allowAnalytics ? 'granted' : 'denied',
        ad_storage: 'denied',
        ad_user_data: 'denied',
        ad_personalization: 'denied',
        functionality_storage: consent.functional === 'yes' ? 'granted' : 'denied',
        personalization_storage: consent.other === 'yes' ? 'granted' : 'denied',
        security_storage: 'granted',
      });
    };

    applyConsentFromCookie();

    const syncConsent: EventListener = () => {
      applyConsentFromCookie();
    };
    document.addEventListener('cookieyes_consent_update', syncConsent);
    document.addEventListener('cookieyes_banner_load', syncConsent);

    return () => {
      document.removeEventListener('cookieyes_consent_update', syncConsent);
      document.removeEventListener('cookieyes_banner_load', syncConsent);
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const config = getGTMConfig();

    if (!config.ga4MeasurementId) {
      console.warn('GA4 Measurement ID not configured');
      return;
    }

    if (!analyticsAllowed) {
      return;
    }

    const ga4MeasurementId = config.ga4MeasurementId;
    const existingScript = document.querySelector(`script[src*="googletagmanager.com/gtag/js?id=${ga4MeasurementId}"]`);

    if (existingScript) {
      return;
    }

    const page = window.location.pathname + window.location.search;
    initializeDataLayer(page);

    window.gtag('js', new Date());
    window.gtag('config', ga4MeasurementId);

    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${ga4MeasurementId}`;
    script.id = 'gtag-script';

    document.head.appendChild(script);
  }, [analyticsAllowed]);

  return <>{children}</>;
};

